/**
 * Disclaimer Component
 *
 * Reusable component that displays a legal disclaimer.
 * Content is loaded from system_settings and can be edited via Admin Panel.
 *
 * Wird global über AppLayout unter jede View gehängt (und manuell am Ende des
 * Chat-Scrollcontainers in AIChatPage). Einzelne Seiten binden ihn nicht mehr
 * selbst ein.
 *
 * Die Styles für den HTML-Inhalt liegen in styles/index.css unter
 * `.disclaimer-content`, damit sie unabhängig von der Zahl der gerenderten
 * Instanzen nur einmal im DOM stehen.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface DisclaimerProps {
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * Fallback-Text, falls der Wert aus system_settings nicht geladen werden kann
 * (Netzwerkfehler, RLS, leerer Datensatz).
 *
 * Spiegelt DEFAULT_DISCLAIMER aus
 * apps/admin-app/src/features/settings/LegalSettingsPage.tsx - dort ist der
 * Wert, den das Admin-Panel beim ersten Speichern in die DB schreibt. Beide
 * Stellen bewusst getrennt gehalten: die user-app soll für einen rein
 * rechtlichen Pflichttext keine Abhängigkeit auf das Admin-Paket bekommen.
 * Wird der Text dort geändert, gehört er hier nachgezogen.
 */
const DEFAULT_DISCLAIMER = `<p>Die in dieser App bereitgestellten Informationen dienen ausschließlich allgemeinen Informations- und Orientierungszwecken. Sie ersetzen in keinem Fall die professionelle Beratung, Diagnose oder Behandlung durch qualifizierte Ärztinnen und Ärzte, Hebammen oder anderes medizinisches Fachpersonal.</p><p>Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Aktualität, Richtigkeit, Vollständigkeit oder Qualität der bereitgestellten Informationen. Die Inhalte können trotz gewissenhafter Prüfung Fehler enthalten oder veraltet sein.</p><p>Jede Schwangerschaft ist individuell. Entscheidungen, die Ihre Gesundheit oder die Gesundheit Ihres Kindes betreffen, sollten stets in Absprache mit medizinischem Fachpersonal getroffen werden. Bei Beschwerden, Unsicherheiten oder Notfällen wenden Sie sich bitte umgehend an Ihre Ärztin, Ihren Arzt, Ihre Hebamme oder den ärztlichen Notdienst.</p><p>Funktionen wie der Produktscanner, der Behördenguide oder andere Hilfsmittel dieser App basieren auf allgemein verfügbaren Daten und Algorithmen. Die Ergebnisse stellen keine medizinische oder rechtliche Beratung dar und sollten nicht als solche verstanden werden.</p><p>Die Nutzung dieser App und ihrer Inhalte erfolgt auf eigene Verantwortung. Wir haften nicht für Schäden, die durch die Nutzung oder Nichtnutzung der bereitgestellten Informationen entstehen.</p>`;

export function Disclaimer({ className = '' }: DisclaimerProps) {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadDisclaimer() {
      try {
        // supabase-js wirft bei Query-Fehlern nicht, sondern liefert `error` -
        // deshalb wird beides geprüft, nicht nur der catch-Zweig.
        const { data, error } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'disclaimer_content')
          .single();

        if (cancelled) return;

        if (error) {
          console.error('Error loading disclaimer:', error);
          setContent(DEFAULT_DISCLAIMER);
          return;
        }

        // Value is stored as JSON string
        const htmlContent =
          data?.value == null
            ? ''
            : typeof data.value === 'string'
              ? data.value
              : String(data.value);

        // Leerer oder fehlender Wert darf den Pflichttext nicht verschwinden
        // lassen - lieber der Default als gar nichts.
        setContent(htmlContent.trim() ? htmlContent : DEFAULT_DISCLAIMER);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading disclaimer:', error);
        setContent(DEFAULT_DISCLAIMER);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadDisclaimer();

    return () => {
      cancelled = true;
    };
  }, []);

  // Während des Ladens bewusst nichts rendern, damit der Text nicht kurz
  // aufblitzt und dann durch den DB-Wert ersetzt wird.
  if (isLoading || !content) {
    return null;
  }

  return (
    <div className={`app-disclaimer mt-12 pt-8 ${className}`}>
      {/* Separator line - 80% width, centered */}
      <div className="flex justify-center mb-8">
        <div className="w-[80%] h-px bg-white/25" />
      </div>

      {/* Disclaimer content */}
      <div
        className="disclaimer-content text-white/50 text-xs leading-relaxed space-y-3 px-4"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
}

export default Disclaimer;
